import { Bedrag } from '@qc/ui';
import type { Evenementcijfers, Waarschuwing } from '@qc/core';

export interface LiveCijfersProps {
  cijfers: Evenementcijfers;
  bijgewerktOp: Date | null;
  waarschuwingen: Waarschuwing[];
}

function Tegel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-tegel border border-rand bg-vlak p-5">
      <p className="text-tekst-zacht">{label}</p>
      <p className="mt-1 text-3xl font-bold">{children}</p>
    </div>
  );
}

export function LiveCijfers({ cijfers, bijgewerktOp, waarschuwingen }: LiveCijfersProps) {
  const drukste = Math.max(1, ...cijfers.perUur.map((uur) => uur.omzetCent));

  return (
    <div>
      {waarschuwingen.length > 0 && (
        <ul className="mb-6 grid gap-2">
          {waarschuwingen.map((waarschuwing) => (
            <li
              key={`${waarschuwing.kraamdeelnameId}-${waarschuwing.soort}`}
              className={`rounded-tegel border p-4 ${
                waarschuwing.dringend
                  ? 'border-fout bg-vlak text-fout'
                  : 'border-waarschuwing bg-vlak text-waarschuwing'
              }`}
            >
              {waarschuwing.melding}
            </li>
          ))}
        </ul>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tegel label="Omzet">
          <Bedrag centen={cijfers.omzetCent} />
        </Tegel>
        <Tegel label="Bonnen">{cijfers.aantalBonnen}</Tegel>
        <Tegel label="Gemiddelde bon">
          <Bedrag centen={cijfers.gemiddeldeBonCent} />
        </Tegel>
        <Tegel label="Jouw commissie">
          <Bedrag centen={cijfers.verwachteCommissieCent} />
        </Tegel>
      </div>

      {cijfers.statiegeldSaldoCent !== 0 && (
        <p className="mb-6 text-tekst-zacht">
          Daarnaast staat er <Bedrag centen={cijfers.statiegeldSaldoCent} /> aan statiegeld uit. Dat
          is geen omzet en telt niet mee in de commissie.
        </p>
      )}

      <h2 className="mb-2 text-xl font-bold">Per kraam</h2>
      <div className="mb-8 overflow-x-auto rounded-tegel border border-rand bg-vlak">
        <table className="w-full min-w-[38rem] text-left">
          <thead className="border-b border-rand text-tekst-zacht">
            <tr>
              <th className="p-4 font-normal">Kraam</th>
              <th className="p-4 text-right font-normal">Bonnen</th>
              <th className="p-4 text-right font-normal">Omzet</th>
              <th className="p-4 text-right font-normal">Gem. bon</th>
              <th className="p-4 text-right font-normal">Commissie</th>
            </tr>
          </thead>
          <tbody>
            {cijfers.perKraam.map((kraam) => (
              <tr key={kraam.kraamdeelnameId} className="border-b border-rand last:border-0">
                <td className="p-4">{kraam.kraamnaam}</td>
                <td className="p-4 text-right tabular-nums">{kraam.aantalBonnen}</td>
                <td className="p-4 text-right">
                  <Bedrag centen={kraam.omzetCent} />
                </td>
                <td className="p-4 text-right text-tekst-zacht">
                  <Bedrag centen={kraam.gemiddeldeBonCent} />
                </td>
                <td className="p-4 text-right font-semibold">
                  <Bedrag centen={kraam.commissieCent} />
                </td>
              </tr>
            ))}
            {cijfers.perKraam.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-tekst-zacht">
                  Er doet nog geen kraam mee.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {cijfers.perUur.length > 0 && (
        <>
          <h2 className="mb-2 text-xl font-bold">Per uur</h2>
          <ul className="mb-6 rounded-tegel border border-rand bg-vlak p-4">
            {cijfers.perUur.map((uur) => (
              <li key={uur.uur} className="flex items-center gap-3 py-1">
                <span className="w-14 shrink-0 tabular-nums text-tekst-zacht">
                  {String(uur.uur).padStart(2, '0')}:00
                </span>
                <span className="h-4 flex-1 overflow-hidden rounded-full bg-vlak-hoog">
                  <span
                    className="block h-full bg-accent"
                    style={{ width: `${(uur.omzetCent / drukste) * 100}%` }}
                  />
                </span>
                <span className="w-28 shrink-0 text-right">
                  <Bedrag centen={uur.omzetCent} />
                </span>
                <span className="w-16 shrink-0 text-right text-tekst-zacht tabular-nums">
                  {uur.aantalBonnen}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {bijgewerktOp !== null && (
        <p className="text-base text-tekst-zacht">
          Bijgewerkt om{' '}
          {new Intl.DateTimeFormat('nl-NL', {
            timeZone: 'Europe/Amsterdam',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(bijgewerktOp)}
          . Ververst zichzelf elke twintig seconden.
        </p>
      )}
    </div>
  );
}
